// src/components/settings/StationGovernance.js
// Session 74u. Stations and trucks, governed from User Management rather than
// Company Settings.
//
// Why not Company Settings, where #262's categories and #198's departments
// live: canAccessCompanySettings is admin/executive, so a branch manager cannot
// open that page at all — and #198 specifies stations as governed by that
// branch's own manager and up, because a truck rename is a local matter with
// none of the cross-branch reporting risk a department rename carries. The two
// halves of #198 were written separately and could not both be true. Colby's
// call at 74u: stations move here, next to the screen that assigns people to
// them and already reads this table.
//
// A branch manager sees only their own branch. Admin and executive see all.

import { useState, useEffect, useCallback } from 'react';
import { canGovernStations } from '../../lib/permissions';
import {
  fetchStations, addStation, renameStation, setStationActive,
  fetchTaxonomyLog, countStationUsage,
} from '../../lib/taxonomy';
import {
  SectionHeading, TaxonomyGroupHeader, TaxonomyRow,
  TaxonomyListShell, TaxonomySheet, AddButton, ChangeLogStrip,
} from './TaxonomyManager';

export default function StationGovernance({ auth, branches, departments, onChanged }) {
  const [rows, setRows] = useState([]);
  const [log, setLog] = useState([]);
  const [sheet, setSheet] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    const [stations, logRows] = await Promise.all([
      fetchStations(),
      fetchTaxonomyLog('station'),
    ]);
    if (stations.error) { setError('Could not load stations: ' + stations.error.message); return; }
    setRows(stations.rows);
    setLog(logRows.rows);
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadConsequence = useCallback(async () => {
    if (!sheet || sheet.mode !== 'rename') return null;
    const counts = await countStationUsage(sheet.row.name, sheet.row.branch_id);
    const parts = [
      counts.users ? `${counts.users} ${counts.users === 1 ? 'person' : 'people'}` : null,
      counts.tools ? `${counts.tools} tool${counts.tools === 1 ? '' : 's'}` : null,
      counts.inspections ? `${counts.inspections} inspection${counts.inspections === 1 ? '' : 's'}` : null,
    ].filter(Boolean);
    if (parts.length === 0) {
      return `Nothing currently uses ${sheet.row.name} at this branch, so this rename affects only the list itself.`;
    }
    return `This also updates ${parts.join(', ')} at this branch currently using ${sheet.row.name}. Other branches are not touched.`;
  }, [sheet]);

  // Only branches this person may govern. For a branch manager that is exactly
  // one; for admin and executive it is all of them.
  const governable = branches.filter(b => canGovernStations(auth, b.id));

  if (governable.length === 0) return null;

  async function handleSubmit(name) {
    setSaving(true);
    setError('');
    const result = sheet.mode === 'rename'
      ? await renameStation(sheet.row.id, name)
      : await addStation(name, sheet.branchId, sheet.department);
    setSaving(false);
    if (result.error) { setError(result.error.message); return; }
    setSheet(null);
    await load();
    if (onChanged) await onChanged();
  }

  async function handleToggle(row) {
    setBusyId(row.id);
    setError('');
    const { error: err } = await setStationActive(row.id, row.is_active === false);
    setBusyId(null);
    if (err) { setError(err.message); return; }
    await load();
    if (onChanged) await onChanged();
  }

  return (
    <div className="card" style={{ marginTop: '1.5rem', padding: '1rem 1.25rem' }}>
      <SectionHeading>Stations and trucks</SectionHeading>

      <p style={{ fontSize: 12, color: '#5C5C5C', margin: '0 0 12px', lineHeight: 1.5 }}>
        These are the stations offered when assigning someone above. A station belongs to one branch
        and one department, and only shows in the picker when both match. Stations are never deleted,
        only deactivated.
      </p>

      {error && (
        <div style={{ background: '#FFEBEE', color: '#A32D2D', borderRadius: 8, padding: '9px 12px', fontSize: 13, marginBottom: 12 }}>
          {error}
        </div>
      )}

      <TaxonomyListShell>
        {governable.map(branch => {
          const stations = rows.filter(r => String(r.branch_id) === String(branch.id));
          return (
            <div key={branch.id}>
              <TaxonomyGroupHeader label={branch.name} meta={`${stations.length} station${stations.length === 1 ? '' : 's'}`} />
              {stations.length === 0 && (
                <div style={{ padding: '10px 12px', borderTop: '0.5px solid #E0E0E0' }}>
                  <span style={{ fontSize: 12, color: '#9A9A9A' }}>No stations at this branch yet.</span>
                </div>
              )}
              {stations.map(station => (
                <TaxonomyRow
                  key={station.id}
                  name={station.name}
                  meta={station.department}
                  isActive={station.is_active !== false}
                  busy={busyId === station.id}
                  onRename={() => { setError(''); setSheet({ mode: 'rename', row: station }); }}
                  onToggleActive={() => handleToggle(station)}
                />
              ))}
            </div>
          );
        })}
      </TaxonomyListShell>

      <AddButton
        label="Add station"
        onClick={() => {
          setError('');
          setSheet({
            mode: 'add',
            branchId: governable[0]?.id ?? null,
            department: departments[0]?.name ?? '',
          });
        }}
      />

      <ChangeLogStrip rows={log} />

      {sheet && (
        <TaxonomySheet
          title={sheet.mode === 'rename' ? 'Rename station' : 'Add station'}
          confirmLabel={sheet.mode === 'rename' ? 'Rename' : 'Add'}
          initialName={sheet.mode === 'rename' ? sheet.row.name : ''}
          loadConsequence={sheet.mode === 'rename' ? loadConsequence : null}
          extraFields={
            sheet.mode === 'add' ? (
              <div>
                <div style={{ marginBottom: 12 }}>
                  <p style={{ fontSize: 13, fontWeight: 500, color: '#0F0F0F', margin: '0 0 6px' }}>Branch</p>
                  <select
                    value={sheet.branchId ?? ''}
                    onChange={e => setSheet(s => ({ ...s, branchId: Number(e.target.value) }))}
                    style={{ width: '100%', height: 44, border: '1px solid #E0E0E0', borderRadius: 8, padding: '0 10px', fontSize: 16, background: 'white' }}
                  >
                    {governable.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                  </select>
                </div>
                <div style={{ marginBottom: 12 }}>
                  <p style={{ fontSize: 13, fontWeight: 500, color: '#0F0F0F', margin: '0 0 6px' }}>Department</p>
                  <select
                    value={sheet.department}
                    onChange={e => setSheet(s => ({ ...s, department: e.target.value }))}
                    style={{ width: '100%', height: 44, border: '1px solid #E0E0E0', borderRadius: 8, padding: '0 10px', fontSize: 16, background: 'white' }}
                  >
                    {departments.map(d => <option key={d.id} value={d.name}>{d.name}</option>)}
                  </select>
                </div>
              </div>
            ) : null
          }
          onCancel={() => { setSheet(null); setError(''); }}
          onSubmit={handleSubmit}
          saving={saving}
          serverError={error}
        />
      )}
    </div>
  );
}
